import { useState, useEffect } from "react";
import { useToasts } from "react-toast-notifications";

import CustomButton from "@/components/desktop/button/CustomButton";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CreateAppOrderTableItem from "@/pages/Planning/OrderApplication/Create/CreateOrderApplicationTableItem";
import CreateAppOrderTableHead from "@/pages/Planning/OrderApplication/Create/CreateOrderApplicationTableHead.jsx";

import { toast } from "@/services/Utils";
import { baseItem } from "@/pages/Planning/OrderApplication/Create/createOrderApplication.constants";
import { calculateItemDoses } from "@/pages/Planning/OrderApplication/Create/create-order-application.helpers";

import classes from "./ProductAlternatives.module.scss";

const ProductAlternatives = ({ open, onClose, data, setData, products, objectives, parentItem, parentItemIndex }) => {
	const { addToast } = useToasts();
	const [localItems, setLocalItems] = useState([]);

	useEffect(() => {
		if (open) {
			setLocalItems(parentItem?.product_options || []);
		} else {
			setLocalItems([]);
		}
	}, [open, parentItem]);

	const localData = {
		...data,
		items: localItems,
		application_order_type_id: data.application_order_type_id,
		estate_id: data.estate_id,
	};

	const handleAddItem = () => {
		setLocalItems((previousItems) => [...previousItems, { ...baseItem }]);
	};

	const handleRemoveItem = (itemIndex) => {
		setLocalItems((previousItems) => previousItems.filter((_, index) => index !== itemIndex));
	};

	const updateLocalItemsAtIndex = (itemIndex, patch) => {
		setLocalItems((previousItems) => previousItems.map((item, index) => (index === itemIndex ? { ...item, ...patch } : item)));
	};

	const localChangeProduct = (itemIndex, element) => {
		const value = element?.value ?? "";
		const selected = products.find((product) => Number(product.value) === Number(value));

		const patch = {
			product_id: value,
			product_code: selected?.code,
			product_name: selected?.label,
			tradename: element?.label,
			color_tag: selected?.color_tag,
			reingreso: selected?.reingreso,
			active_ingredient: selected?.active_ingredient,
			active_ingredients: selected?.active_ingredients,
			aptitudes: selected?.aptitudes,
			unit_measurement: selected?.unit_measurement,
			url_tag: selected?.url_tag,
		};

		updateLocalItemsAtIndex(itemIndex, patch);
	};

	const localChangeObjective = (itemIndex, element) => {
		const value = element?.value ?? "";
		const patch = {
			application_orders_objetives_id: value,
			application_orders_objetives_name: element?.label,
		};

		updateLocalItemsAtIndex(itemIndex, patch);
	};

	const localChangeLackDays = (itemIndex, value) => {
		updateLocalItemsAtIndex(itemIndex, { lack_days: value });
	};

	const localCalculate = (itemIndex, patch, type) => {
		const context = {
			wetness: Number(data?.machineries?.[0]?.wetness) || 0,
			machined: Number(data?.machineries?.[0]?.machined) || 0,
			totalArea: (data?.business_centers || []).reduce((sum, businessCenter) => {
				const parsedArea = Number(String(businessCenter.area_select ?? "").replace(",", "."));
				return sum + (isNaN(parsedArea) ? 0 : parsedArea);
			}, 0),
			appTypeId: data?.application_order_type_id,
		};

		setLocalItems((previousItems) =>
			previousItems.map((item, index) => (index !== itemIndex ? item : calculateItemDoses({ ...item, ...patch }, context, type))),
		);
	};

	const localChangeDoseX100Ml = (itemIndex, value) => {
		localCalculate(itemIndex, { dose_100L: value }, "dose_100L");
	};

	const localChangeDoseHa = (itemIndex, value) => {
		localCalculate(itemIndex, { dose_ha: value }, "dose_ha");
	};

	const handleConfirm = () => {
		const validItems = localItems.filter((item) => item.product_id).map(({ product_options: _, ...item }) => item);

		setData((previousData) => {
			let currentIndex = 0;
			const newItems = previousData.items.map((item) => {
				if (item.isCopy) {
					return item;
				}
				if (currentIndex === parentItemIndex) {
					currentIndex++;
					return { ...item, product_options: validItems };
				}
				currentIndex++;
				return item;
			});
			return { ...previousData, items: newItems };
		});

		if (validItems.length > 0) {
			toast(addToast, "Producto opcional agregado correctamente", "success");
		}

		onClose();
	};

	return (
		<CustomModalForm
			open={open}
			onClose={onClose}
			title={`Opciones para producto ${parentItem?.tradename || ""}`}
			size="xxl"
			width="90%"
			closeOnBackdrop={false}
			closeOnEsc={false}
			showSave={true}
			textForm="Confirmar"
			onSubmit={handleConfirm}
		>
			<div className={classes.bodyModal}>
				<CreateAppOrderTableHead data={localData} />
				<CreateAppOrderTableItem
					data={localData}
					products={products}
					objectives={objectives}
					changeProduct={localChangeProduct}
					changeObjective={localChangeObjective}
					changeLackDays={localChangeLackDays}
					changeDoseX100Ml={localChangeDoseX100Ml}
					changeDoseHa={localChangeDoseHa}
					isCopy={false}
					calculateTotal={() => {}}
					onRemoveItem={handleRemoveItem}
					selectMenuPlacement="bottom"
				/>

				<div className={classes.actionContainer}>
					<CustomButton type="button" variant="secondary" onClick={handleAddItem} className={classes.addOptionButton}>
						Agregar opción
					</CustomButton>
				</div>
			</div>
		</CustomModalForm>
	);
};

export default ProductAlternatives;
